import { useRef, useEffect, useState, useCallback } from "react";

/**
 * How quickly the view catches up with new content: most of the way within about this long. Text grows a
 * line at a time, and snapping to each new line jolts everything up by a line height; easing glides it.
 */
export const AUTO_SCROLL_FOLLOW_MS = 90;

export const useAutoScroll = (content: string) => {
  const threshold = 50;
  const scrollDelay = 100;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUserScrollingRef = useRef(false);
  isUserScrollingRef.current = isUserScrolling;
  const frameRef = useRef<number | null>(null);
  // Where the follow last put the view, so its own scroll events are not taken for the user's.
  const followedTopRef = useRef<number | null>(null);

  const isAtBottom = useCallback(
    (element: HTMLDivElement) => {
      return element.scrollHeight - element.scrollTop - element.clientHeight < threshold;
    },
    [threshold],
  );

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      // Mid-glide the view is briefly short of the bottom; that is the follow, not the user scrolling away.
      if (
        followedTopRef.current !== null &&
        Math.abs(scrollContainerRef.current.scrollTop - followedTopRef.current) <= 2
      ) {
        return;
      }
      const atBottom = isAtBottom(scrollContainerRef.current);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      if (!atBottom) {
        setIsUserScrolling(true);
      } else {
        scrollTimeoutRef.current = setTimeout(() => {
          setIsUserScrolling(false);
        }, scrollDelay);
      }
    }
  }, [isAtBottom, scrollDelay]);

  const resetUserScrolling = useCallback(() => {
    setIsUserScrolling(false);
  }, []);

  // Eases the view down to the bottom, a frame at a time, until it arrives or the user takes over.
  const follow = useCallback(() => {
    if (frameRef.current !== null) return;
    let last = performance.now();
    const step = (now: number) => {
      const element = scrollContainerRef.current;
      frameRef.current = null;
      if (!element || isUserScrollingRef.current) return;
      const bottom = element.scrollHeight - element.clientHeight;
      const gap = bottom - element.scrollTop;
      if (gap <= 0) return;
      const elapsed = Math.min(now - last, 64);
      last = now;
      // At least a pixel a frame, or a browser rounding scrollTop to whole pixels would never arrive; the
      // last pixel lands exactly on the bottom.
      const next = Math.min(
        bottom,
        element.scrollTop + Math.max(1, gap * (1 - Math.exp(-elapsed / AUTO_SCROLL_FOLLOW_MS))),
      );
      const top = gap <= 1 ? bottom : next;
      followedTopRef.current = top;
      element.scrollTop = top;
      frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
  }, []);

  // Auto-scroll only when content *grows*
  const previousContentLengthRef = useRef(content.length);
  useEffect(() => {
    const grew = content.length > previousContentLengthRef.current;
    previousContentLengthRef.current = content.length;
    const element = scrollContainerRef.current;
    if (!grew || !element || isUserScrolling) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      element.scrollTop = element.scrollHeight;
      return;
    }
    follow();
  }, [content, isUserScrolling, follow]);

  // The reader taking hold of the pane (a finger dragging, a wheel turned up) stops the glide at once.
  // Waiting for their scroll to show up as distance from the bottom leaves the glide writing scrollTop
  // under their finger, which fights iOS momentum scrolling. Following resumes once they are back at
  // the bottom (handleScroll).
  const handleTouchMove = useCallback(() => {
    isUserScrollingRef.current = true;
    setIsUserScrolling(true);
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);
  const handleWheel = useCallback(
    (event: { deltaY: number }) => {
      if (event.deltaY < 0) handleTouchMove();
    },
    [handleTouchMove],
  );

  // Cleanup timeout and any glide in progress
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  // The reader asked to catch up: follow again, gliding down to the latest text.
  const scrollToLatest = useCallback(() => {
    isUserScrollingRef.current = false;
    setIsUserScrolling(false);
    const element = scrollContainerRef.current;
    if (!element) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      element.scrollTop = element.scrollHeight;
      return;
    }
    follow();
  }, [follow]);

  return {
    scrollContainerRef,
    handleScroll,
    handleTouchMove,
    handleWheel,
    resetUserScrolling,
    scrollToLatest,
    isUserScrolling,
  };
};
